import { Typography, Menu, MenuItem } from "@avenue-8/sidekick-ui";
import styled from "@emotion/styled";
import { useCallback, useRef, useState } from "react";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import EditIcon from "@mui/icons-material/Edit";
import { KeyFeaturesSectionConfig } from "@cma/presentation-generation-logic";
import { KeyFeatureEditableInput } from "./KeyFeatureEditableInput";
import { EditableKeyFeatures } from "../helpers/key-features-form-helpers";

const KeyFeatureItemContainer = styled.button<{ $editMode?: boolean }>`
  background: none;
  border: none;
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  row-gap: 0.25rem;
  padding: 1.5rem 0;
  border-radius: 0.5rem;

  &:hover {
    cursor: ${(p) => (p.$editMode ? "pointer" : "default")};
    background-color: ${(p) =>
      p.$editMode ? "rgba(128, 128, 128, 0.12)" : "transparent"};
  }
`;

const KeyFeatureValue = styled(Typography)`
  font-size: ${(p) => p.theme.text.fontSizes.l};
  word-break: break-word;
`;

const TitleReadonly = styled.div`
  position: relative;
`;

type Props = {
  title: React.ReactNode;
  value: string;
  editableValue?: boolean;
  configName: keyof KeyFeaturesSectionConfig;
  valueName?: EditableKeyFeatures;
  onDelete?: (keyFeatureName: keyof KeyFeaturesSectionConfig) => void;
  onEdit?: (valueName: EditableKeyFeatures, value: string) => void;
  onEditError?: (error: string | null) => void;
  editMode?: boolean;
};

export const KeyFeatureItem = (props: Props) => {
  const {
    title,
    value,
    configName,
    valueName,
    onDelete,
    onEdit,
    onEditError,
    editableValue,
    editMode,
  } = props;
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const titleRef = useRef<HTMLElement | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const open = Boolean(anchorEl);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(titleRef.current || event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleDelete = useCallback(() => {
    if (onDelete) {
      onDelete(configName);
      setAnchorEl(null);
    }
  }, [configName, onDelete]);

  const handleStartEdit = useCallback(() => {
    setAnchorEl(null);
    setTimeout(() => {
      setIsEditing(true);
    }, 100);
  }, []);

  const handleSaveEdit = useCallback(
    (newValue: string) => {
      if (onEdit && valueName) {
        onEdit(valueName, newValue);
      }
      setIsEditing(false);
    },
    [onEdit, valueName]
  );

  const handleCancelEdit = useCallback(() => {
    setIsEditing(false);
  }, []);

  const isReadOnly = !onDelete && !onEdit;

  if (isReadOnly) {
    return (
      <KeyFeatureItemContainer>
        <TitleReadonly>
          <Typography variant="body2">{title}</Typography>
        </TitleReadonly>
        <KeyFeatureValue>{value}</KeyFeatureValue>
      </KeyFeatureItemContainer>
    );
  }

  return (
    <>
      <KeyFeatureItemContainer
        $editMode={editMode}
        onClick={handleClick}
        tabIndex={0}
        aria-controls={open ? "key-features-title-menu" : undefined}
        aria-haspopup="true"
        aria-expanded={open ? "true" : undefined}
      >
        <Typography variant="body2" ref={titleRef}>
          {title}
        </Typography>

        {isEditing && valueName ? (
          <KeyFeatureEditableInput
            key={`edit-${valueName}`}
            valueName={valueName}
            value={value}
            onSave={handleSaveEdit}
            onCancel={handleCancelEdit}
            onError={onEditError}
            autoFocus
          />
        ) : (
          <KeyFeatureValue>{value}</KeyFeatureValue>
        )}
      </KeyFeatureItemContainer>
      <Menu
        id="key-features-title-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "center",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "center",
        }}
      >
        {editableValue && (
          <MenuItem onClick={handleStartEdit}>
            <EditIcon fontSize="small" /> Edit
          </MenuItem>
        )}
        <MenuItem onClick={handleDelete}>
          <VisibilityOffIcon fontSize="small" /> Hide
        </MenuItem>
      </Menu>
    </>
  );
};
